import type { ApiProblem } from './types'

const API_ROOT = '/api/v1'
const unsafeMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

const problemMessages: Record<string, string> = {
  'workout.closed': 'Dokončený trénink už nelze měnit. Pro další úpravy nejdřív otevři nový nebo obnovený trénink.',
  'workout.abandon_invalid_state': 'Opustit lze pouze právě rozpracovaný trénink.',
  'workout.abandon.invalid': 'Potvrzení opuštění tréninku není platné. Opiš přesně zobrazený text.',
  'workout.set_band_required': 'Pro tento cvik vyber použitou odporovou gumu.',
  'workout.set_band_unknown': 'Vybraná odporová guma není v tvém vybavení.',
  'workout.set_load_required': 'Pro tento cvik zadej skutečně použitou zátěž v kilogramech.',
  'workout.set_modifier_not_supported': 'Tento cvik zadaný typ zátěže nepodporuje.',
  'workout.set_correction_invalid': 'Oprava série není úplná. Zkontroluj výkon a doplň stručný důvod.',
  'workout.set_correction_invalid_state': 'Sérii lze opravit jen v rozpracovaném nebo dokončeném tréninku.',
  'workout.set_not_found': 'Uložená série nebyla v tomto tréninku nalezena.',
  'unable.regression_not_allowed': 'Vybraná lehčí varianta není pro tento cvik bezpečně povolená.',
  'decision.stale': 'Trénink mezitím pokračoval. Starý návrh byl zahozen a coach použije aktuální stav.',
  'plan.lifecycle_required': 'Tento plán používá bezpečný release proces. Aktivaci nebo návrat proveď v panelu Owner plán v4.',
  'response.invalid_json': 'Server vrátil neplatnou odpověď. Zkus akci zopakovat.',
  'response.non_json': 'Server požadavek nedokončil správně. Zkus akci zopakovat.',
}

function safeProblemMessage(problem: ApiProblem): string {
  if (problem.code && problemMessages[problem.code]) return problemMessages[problem.code]
  const candidate = problem.detail ?? problem.message ?? problem.title
  if (!candidate || candidate.trim().startsWith('<') || candidate.trim().startsWith('{')) {
    return 'Požadavek se nepodařilo dokončit. Zkus to znovu.'
  }
  return candidate
}
export class ApiError extends Error {
  readonly status: number
  readonly problem: ApiProblem

  constructor(status: number, problem: ApiProblem) {
    super(safeProblemMessage(problem))
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

export function isOfflineApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
    && error.status === 503
    && error.problem.code === 'offline.unavailable'
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`
  const part = document.cookie.split('; ').find((item) => item.startsWith(prefix))
  return part ? decodeURIComponent(part.slice(prefix.length)) : null
}

export function createIdempotencyKey(): string {
  return crypto.randomUUID()
}

type RequestOptions = RequestInit & {
  idempotencyKey?: string
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const headers = new Headers(options.headers)

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (unsafeMethods.has(method)) {
    const csrf = readCookie('repinu_csrf')
    if (csrf) headers.set('X-CSRF-Token', csrf)
    headers.set('Idempotency-Key', options.idempotencyKey ?? createIdempotencyKey())
  }

  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'same-origin',
  })

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? ''
  const isJson = contentType.includes('application/json') || contentType.includes('+json')
  let payload: unknown
  if (isJson) {
    try {
      payload = await response.json()
    } catch {
      payload = { code: 'response.invalid_json', title: 'Server vrátil neplatnou odpověď.' }
    }
  } else {
    const detail = (await response.text()).trim()
    payload = {
      code: 'response.non_json',
      title: detail.startsWith('<') || detail.startsWith('{')
        ? 'Server požadavek nedokončil správně.'
        : detail || 'Server požadavek nedokončil správně.',
    }
  }

  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event('repinu:unauthorized'))
    throw new ApiError(response.status, payload as ApiProblem)
  }

  return payload as T
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value)
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return safeProblemMessage(error.problem)
  if (error instanceof Error) {
    const message = error.message.trim()
    return message.startsWith('<') || message.startsWith('{')
      ? 'Něco se nepodařilo. Zkus to znovu.'
      : message
  }
  return 'Něco se nepodařilo. Zkus to znovu.'
}